import { useMemo } from 'react';

import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import {
  MULTI_INSTANCE_TOOL_GROUP_KINDS,
  SMART_ASSISTANT_CONFIGURABLE_TOOL_GROUPS_BY_TYPE
} from 'shared/constants/tool-group';
import { SmartAssistantType } from 'shared/interfaces/SmartAssistant';
import { ToolConfig, ToolGroup, ToolKind } from 'shared/interfaces/tools/Tool';

interface UseToolGroupsProps {
  smartAssistantType: SmartAssistantType;
  existingToolConfigs: ToolConfig[];
}

export function useToolGroups({ smartAssistantType, existingToolConfigs }: UseToolGroupsProps) {
  const [subscription] = useSubscriptionContext();
  const availableToolKinds = subscription.product.enabledTools || [];
  const attachedToolKinds: ToolKind[] = existingToolConfigs.map((toolConfig) => toolConfig.kind);

  const { unavailableToolGroups, attachedToolGroups, unattachedToolGroups } = useMemo(() => {
    const configurableToolGroups = SMART_ASSISTANT_CONFIGURABLE_TOOL_GROUPS_BY_TYPE[smartAssistantType];

    // Split tool groups into single and multi instance
    const singleInstanceToolGroups = configurableToolGroups.filter(
      (toolGroup) => !MULTI_INSTANCE_TOOL_GROUP_KINDS.includes(toolGroup.kind)
    );
    const multiInstanceToolGroups = configurableToolGroups.filter((toolGroup) =>
      MULTI_INSTANCE_TOOL_GROUP_KINDS.includes(toolGroup.kind)
    );

    /**
     * UNAVAILABLE TOOL GROUPS
     *
     * Tool groups that are configurable for this smart assistant type but are not
     * included in the user's current subscription plan. These will be shown in the UI
     * with upgrade prompts or locked states to encourage plan upgrades.
     */
    const unavailableToolGroups = configurableToolGroups.filter((toolGroup) =>
      toolGroup.toolKinds.some((toolKind) => !availableToolKinds.includes(toolKind))
    );

    /**
     * ATTACHED TOOL GROUPS
     *
     * Tool groups that have existing configurations and should be displayed as "active"
     * or "configured" in the UI. This includes:
     *
     * 1. Single Instance Tools (e.g., Lead Collection, Email Handoff):
     *    - One tool group entry containing all related tool configurations
     *    - Only included if ALL required tool config kinds are configured
     *    - Example: Lead Collection tool group with its single tool config
     *
     * 2. Multi Instance Tools (e.g., Custom API Request):
     *    - Each individual tool config becomes its own tool group entry
     *    - Allows users to see and manage each API tool config separately
     *    - Example: "Weather API" and "Payment API" as separate tool group entries
     */
    const attachedToolGroups: ToolGroup[] = [
      ...singleInstanceToolGroups
        .filter((toolGroup) => toolGroup.toolKinds.every((toolKind) => attachedToolKinds.includes(toolKind)))
        .map((toolGroup) => ({
          ...toolGroup,
          toolConfigs: existingToolConfigs.filter((config) => toolGroup.toolKinds.includes(config.kind))
        })),
      ...existingToolConfigs
        .filter((config) => multiInstanceToolGroups.some((toolGroup) => toolGroup.toolKinds.includes(config.kind)))
        .map((config) => {
          const toolGroup = multiInstanceToolGroups.find((group) => group.toolKinds.includes(config.kind));
          if (!toolGroup) return null;
          return {
            ...toolGroup,
            toolConfigs: [config]
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null)
    ];

    /**
     * UNATTACHED TOOL GROUPS
     *
     * Tool groups that are available in the user's subscription but are not yet
     * configured. These are shown in the "Add Tool" UI sections. This includes:
     *
     * 1. Multi Instance Tools:
     *    - Always included, even if some instances are already configured
     *    - Allows users to add additional instances (e.g., more Custom API tools)
     *
     * 2. Single Instance Tools:
     *    - Only included if they have no existing configurations
     *    - Once configured, they move to the attached list
     */
    const unattachedToolGroups = [
      ...multiInstanceToolGroups.filter((toolGroup) =>
        toolGroup.toolKinds.every((toolKind) => availableToolKinds.includes(toolKind))
      ),
      ...singleInstanceToolGroups.filter(
        (toolGroup) =>
          toolGroup.toolKinds.every((toolKind) => availableToolKinds.includes(toolKind)) &&
          !toolGroup.toolKinds.every((toolKind) => attachedToolKinds.includes(toolKind))
      )
    ];

    return {
      unavailableToolGroups,
      attachedToolGroups,
      unattachedToolGroups
    };
  }, [availableToolKinds, smartAssistantType, attachedToolKinds, existingToolConfigs]);

  return { unavailableToolGroups, attachedToolGroups, unattachedToolGroups };
}
